import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api, BACKEND_URL, TOKEN_KEY } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Receipt, Download, MagnifyingGlass, ChartBar, Tag } from "@phosphor-icons/react";

/**
 * Receipts ledger — admin view for end-of-month reconciliation against the UPI bank statement.
 * Supports text search, status filter, ISO date range, and a per-course revenue summary.
 */
export default function Receipts() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("enrolled");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [revenue, setRevenue] = useState(null);
  const [showRevenue, setShowRevenue] = useState(false);

  useEffect(() => {
    api.get("/registrations", { params: { status: statusFilter } }).then((r) => setItems(r.data));
  }, [statusFilter]);

  // Auto-load revenue summary whenever date range or visibility changes
  useEffect(() => {
    if (!showRevenue) return;
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api
      .get("/registrations/revenue/by-course", { params })
      .then((r) => setRevenue(r.data))
      .catch(() => setRevenue(null));
  }, [showRevenue, dateFrom, dateTo]);

  const dateField = statusFilter === "enrolled" ? "enrolled_at" : "submitted_at";

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((r) => {
      // Text search
      if (needle) {
        const haystack = [r.full_name, r.student_id, r.payment_utr, r.course_name, r.phone]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      // Date range filter (client-side, against the relevant date field)
      const dval = (r[dateField] || "").slice(0, 10);
      if (dateFrom && dval && dval < dateFrom) return false;
      if (dateTo && dval && dval > dateTo) return false;
      return true;
    });
  }, [items, q, dateFrom, dateTo, dateField]);

  const total = useMemo(
    () => filtered.reduce((sum, r) => sum + (Number(r.payment_amount) || 0), 0),
    [filtered]
  );

  const csvParams = useMemo(() => {
    const p = new URLSearchParams({ status: statusFilter });
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    return p.toString();
  }, [statusFilter, dateFrom, dateTo]);

  const downloadCsv = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(`${BACKEND_URL}/api/registrations/export.csv?${csvParams}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sms-${statusFilter}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadDiscountAudit = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    const res = await fetch(`${BACKEND_URL}/api/registrations/discount-audit.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `discount-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const clearDates = () => {
    setDateFrom("");
    setDateTo("");
  };

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-[var(--sms-primary)] mb-3">Ledger</div>
          <h1 className="font-display text-5xl font-black tracking-tighter">Receipts &amp; reconciliation.</h1>
          <p className="text-slate-500 mt-2" data-testid="receipts-summary">
            {filtered.length} record{filtered.length === 1 ? "" : "s"} · ₹ {total.toLocaleString("en-IN")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowRevenue((v) => !v)}
            data-testid="btn-toggle-revenue"
            className="sms-btn-secondary inline-flex items-center gap-2"
          >
            <ChartBar size={14} weight="bold" /> {showRevenue ? "Hide revenue" : "Revenue by course"}
          </button>
          {me?.role === "admin" && (
            <button
              onClick={downloadDiscountAudit}
              data-testid="btn-discount-audit"
              className="sms-btn-secondary inline-flex items-center gap-2"
              title="Tamper-proof history of every discount applied / updated / removed"
            >
              <Tag size={14} weight="duotone" /> Discount audit
            </button>
          )}
          <button onClick={downloadCsv} data-testid="btn-export-csv" className="sms-btn-primary inline-flex items-center gap-2">
            <Download size={16} weight="bold" /> Download CSV
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="sms-input pl-9"
            placeholder="Search by name, student ID, UTR, course, phone…"
            value={q}
            data-testid="receipts-search"
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-1 border border-slate-200">
          {["enrolled", "approved", "rejected", "pending"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-4 py-2 font-mono-sms text-[10px] uppercase tracking-[0.2em] ${statusFilter === s ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Date range row */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <span className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
          {dateField.replace("_", " ")} between
        </span>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          data-testid="receipts-date-from"
          className="sms-input max-w-[170px]"
        />
        <span className="text-slate-400">→</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          data-testid="receipts-date-to"
          className="sms-input max-w-[170px]"
        />
        {(dateFrom || dateTo) && (
          <button
            onClick={clearDates}
            data-testid="receipts-clear-dates"
            className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[var(--sms-primary)]"
          >
            Clear
          </button>
        )}
      </div>

      {/* Revenue summary */}
      {showRevenue && (
        <div className="border border-slate-200 mb-6" data-testid="revenue-summary">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)]">
              Revenue by course (enrolled){dateFrom || dateTo ? ` · ${dateFrom || "…"} → ${dateTo || "…"}` : ""}
            </div>
            {revenue?.totals && (
              <div className="font-mono-sms text-xs">
                {revenue.totals.count} enrolled · ₹{Number(revenue.totals.total).toLocaleString("en-IN")}
              </div>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white">
              <tr className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <th className="text-left px-4 py-2 font-medium">Code</th>
                <th className="text-left px-4 py-2 font-medium">Course</th>
                <th className="text-right px-4 py-2 font-medium">Enrolled</th>
                <th className="text-right px-4 py-2 font-medium">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {(!revenue || revenue.by_course.length === 0) && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">No enrolment in this range.</td></tr>
              )}
              {revenue?.by_course.map((b) => (
                <tr key={b.course_id} className="border-t border-slate-200" data-testid={`revenue-row-${b.course_code || b.course_id}`}>
                  <td className="px-4 py-2 font-mono-sms text-xs">{b.course_code || "—"}</td>
                  <td className="px-4 py-2">{b.course_name}</td>
                  <td className="px-4 py-2 text-right font-mono-sms">{b.count}</td>
                  <td className="px-4 py-2 text-right font-mono-sms">₹{Number(b.total).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="border border-slate-200" data-testid="receipts-table-wrapper">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="text-left px-4 py-3 font-medium">Student ID</th>
              <th className="text-left px-4 py-3 font-medium">Name</th>
              <th className="text-left px-4 py-3 font-medium">Course</th>
              <th className="text-right px-4 py-3 font-medium">Amount</th>
              <th className="text-left px-4 py-3 font-medium">UTR</th>
              <th className="text-left px-4 py-3 font-medium">Paid at</th>
              <th className="text-left px-4 py-3 font-medium">Receipt</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No matching records.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`receipt-row-${r.student_id || r.id}`} className="border-t border-slate-200 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono-sms text-xs">{r.student_id || "—"}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.full_name}</div>
                  <div className="text-xs text-slate-500">{r.phone}</div>
                </td>
                <td className="px-4 py-3">{r.course_name || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-sms">₹{Number(r.payment_amount || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 font-mono-sms text-xs">{r.payment_utr || "—"}</td>
                <td className="px-4 py-3 font-mono-sms text-xs text-slate-500">
                  {r.payment_paid_at ? new Date(r.payment_paid_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3">
                  {r.status === "enrolled" && r.payment_token ? (
                    <a
                      href={`${BACKEND_URL}/api/payments/${r.payment_token}/receipt`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`receipt-pdf-${r.student_id}`}
                      className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-[var(--sms-primary)] hover:underline inline-flex items-center gap-1"
                    >
                      <Receipt size={12} weight="bold" /> PDF
                    </a>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
